import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Bot, X, ListTodo, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { GarvChatTab } from "./garv/GarvChatTab";
import { GarvBoardTab } from "./garv/GarvBoardTab";

const GREETING = { role: "assistant", reply: "Hi, I'm GARV 👋 — tell me what you need. e.g. \"Remind Khushboo to arrange pickup for the email I got yesterday from L&T, due tomorrow.\"" };

export function GarvWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat");
  const [msgs, setMsgs] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const endRef = useRef(null);
  const navTimer = useRef(null);

  // Draggable floating button — position persisted so users can place it anywhere.
  const FAB_DEFAULT = () => ({ x: 24, y: (typeof window !== "undefined" ? window.innerHeight - 88 : 600) });
  const [pos, setPos] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("garv_fab_pos")); if (s && typeof s.x === "number" && typeof s.y === "number") return s; } catch { /* ignore */ }
    return FAB_DEFAULT();
  });
  const drag = useRef({ active: false, moved: false, offX: 0, offY: 0, startX: 0, startY: 0, w: 0, h: 0 });

  useEffect(() => {
    const clamp = () => setPos((p) => ({ x: Math.max(8, Math.min(window.innerWidth - 80, p.x)), y: Math.max(8, Math.min(window.innerHeight - 72, p.y)) }));
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, []);

  function onFabPointerDown(e) {
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    const r = el.getBoundingClientRect();
    drag.current = { active: true, moved: false, offX: e.clientX - r.left, offY: e.clientY - r.top, startX: e.clientX, startY: e.clientY, w: r.width, h: r.height };
  }
  function onFabPointerMove(e) {
    const d = drag.current;
    if (!d.active) return;
    if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 4) d.moved = true;
    const x = Math.max(8, Math.min(window.innerWidth - d.w - 8, e.clientX - d.offX));
    const y = Math.max(8, Math.min(window.innerHeight - d.h - 8, e.clientY - d.offY));
    setPos({ x, y });
  }
  function onFabPointerUp() {
    const d = drag.current;
    d.active = false;
    if (!d.moved) setOpen(true);
    else setPos((p) => { localStorage.setItem("garv_fab_pos", JSON.stringify(p)); return p; });
  }

  function panelStyle() {
    const vw = window.innerWidth, vh = window.innerHeight;
    const pw = Math.min(400, vw * 0.92), ph = Math.min(620, vh * 0.7);
    const left = Math.max(8, Math.min(vw - pw - 8, pos.x));
    const top = Math.max(8, Math.min(vh - ph - 8, pos.y));
    return { left, top, width: pw, height: ph };
  }

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);
  useEffect(() => { if (open && tab === "board") loadTasks(); }, [open, tab]);

  async function loadTasks() {
    try { const { data } = await api.get("/garv/tasks?scope=open"); setTasks(data); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.error("GARV: failed to load tasks", e?.message || e); }
  }

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setMsgs((m) => [...m, { role: "user", reply: text }]);
    setInput("");
    setSending(true);
    if (navTimer.current) { clearTimeout(navTimer.current); navTimer.current = null; }
    try {
      const { data } = await api.post("/garv/chat", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", ...data }]);
      if (data.created_tasks?.length) toast.success(`GARV added ${data.created_tasks.length} reminder(s) to the board`);
      if (data.created_reminders?.length) {
        toast.success(`GARV set ${data.created_reminders.length} reminder timer(s) — check the bell 🔔`);
        window.dispatchEvent(new Event("timers-changed"));
      }
      if (data.navigate?.path) {
        navTimer.current = setTimeout(() => { setOpen(false); navigate(data.navigate.path); }, 600);
      }
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", reply: formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again." }]);
    } finally { setSending(false); }
  }

  async function markDone(id) {
    try { await api.patch(`/garv/tasks/${id}`, { status: "done" }); setTasks((t) => t.filter((x) => x.id !== id)); toast.success("Marked done"); }
    catch { toast.error("Could not update"); }
  }

  return (
    <>
      {!open && (
        <button data-testid="garv-fab"
          onPointerDown={onFabPointerDown} onPointerMove={onFabPointerMove} onPointerUp={onFabPointerUp}
          style={{ left: pos.x, top: pos.y }}
          className="fixed z-50 flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-[#1A1A18] text-white shadow-xl hover:bg-[#C84B31] transition-colors touch-none select-none cursor-grab active:cursor-grabbing">
          <span className="w-8 h-8 rounded-full bg-[#C84B31] flex items-center justify-center shrink-0"><Bot className="w-5 h-5" /></span>
          <span className="font-headings font-bold tracking-wide">GARV</span>
        </button>
      )}

      {open && (
        <div style={panelStyle()} className="fixed z-50 bg-white border border-[#E5E3DB] shadow-2xl rounded-lg flex flex-col overflow-hidden" data-testid="garv-panel">
          <div className="px-4 py-3 bg-[#1A1A18] text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#C84B31] flex items-center justify-center"><Bot className="w-5 h-5" /></span>
              <div><div className="font-headings font-bold leading-none">GARV</div><div className="text-[10px] text-white/60 mt-0.5">Your ops assistant</div></div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="garv-close"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex border-b border-[#E5E3DB] text-sm">
            <button onClick={() => setTab("chat")} data-testid="garv-tab-chat" className={`flex-1 py-2 flex items-center justify-center gap-1.5 ${tab === "chat" ? "text-[#C84B31] border-b-2 border-[#C84B31] font-medium" : "text-[#68665E]"}`}><MessageSquare className="w-4 h-4" /> Chat</button>
            <button onClick={() => setTab("board")} data-testid="garv-tab-board" className={`flex-1 py-2 flex items-center justify-center gap-1.5 ${tab === "board" ? "text-[#C84B31] border-b-2 border-[#C84B31] font-medium" : "text-[#68665E]"}`}><ListTodo className="w-4 h-4" /> Board</button>
          </div>

          {tab === "chat" ? (
            <GarvChatTab msgs={msgs} sending={sending} input={input} setInput={setInput} send={send} endRef={endRef}
              onQuickAction={(qa) => {
                if (qa?.type === "navigate" && qa.path) { setOpen(false); navigate(qa.path); }
                else if (qa?.type === "send" && qa.message) { setInput(qa.message); setTimeout(send, 0); }
              }} />
          ) : (
            <GarvBoardTab tasks={tasks} markDone={markDone} onOpenFull={() => { setOpen(false); navigate("/reminders"); }} />
          )}
        </div>
      )}
    </>
  );
}

export default GarvWidget;
